/**
 * MinistrareIA - Support Tickets Page
 * Lists and manages support tickets created by the AI agent.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  LifeBuoy,
  Search,
  ChevronLeft,
  ChevronRight,
  X,
  CheckCircle,
  Clock,
  XCircle,
  PlayCircle,
  RefreshCw,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import ticketService from '../../services/wa-business/ticketService';

const PAGE_SIZE = 25;

const STATUS_CONFIG = {
  open: { label: 'Aberto', color: '#ef4444', icon: Clock },
  in_progress: { label: 'Em andamento', color: '#f59e0b', icon: PlayCircle },
  resolved: { label: 'Resolvido', color: '#22c55e', icon: CheckCircle },
  closed: { label: 'Fechado', color: '#64748b', icon: XCircle },
};

const FILTER_OPTIONS = [
  { id: 'all', label: 'Todos' },
  { id: 'open', label: 'Abertos' },
  { id: 'in_progress', label: 'Em andamento' },
  { id: 'resolved', label: 'Resolvidos' },
  { id: 'closed', label: 'Fechados' },
];

const TicketsPage = () => {
  const { colors } = useTheme();
  const [tickets, setTickets] = useState([]);
  const [counts, setCounts] = useState({ open: 0, in_progress: 0, resolved: 0, closed: 0 });
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [phoneFilter, setPhoneFilter] = useState('');
  const [page, setPage] = useState(1);
  const [selectedTicket, setSelectedTicket] = useState(null);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const [list, c] = await Promise.all([
        ticketService.list({ status: statusFilter, phone: phoneFilter || undefined, limit: 200 }),
        ticketService.getCounts(),
      ]);
      setTickets(list.tickets || []);
      setCounts(c || { open: 0, in_progress: 0, resolved: 0, closed: 0 });
    } catch (e) {
      console.error('Error loading tickets:', e);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, phoneFilter]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  // Avisos por WhatsApp para a equipe: cada número escolhe o que quer receber
  const [notifyRows, setNotifyRows] = useState([]);          // [{ phone, kinds: [] }]
  const [notifyTypes, setNotifyTypes] = useState({});        // { ticket: "descrição", ... }
  const [newPhone, setNewPhone] = useState('');
  const [notifyMsg, setNotifyMsg] = useState(null);
  const [notifyBusy, setNotifyBusy] = useState(false);

  useEffect(() => {
    ticketService.getNotifyConfig()
      .then((c) => {
        setNotifyTypes(c.tipos_disponiveis || { ticket: 'Novo chamado de suporte' });
        setNotifyRows(c.numeros || (c.phones || []).map((p) => ({ phone: p, kinds: ['ticket'] })));
      })
      .catch(() => {});
  }, []);

  const toggleKind = (phone, kind) => {
    setNotifyRows((rows) => rows.map((r) => {
      if (r.phone !== phone) return r;
      const has = (r.kinds || []).includes(kind);
      return { ...r, kinds: has ? r.kinds.filter((k) => k !== kind) : [...(r.kinds || []), kind] };
    }));
  };

  const addPhone = () => {
    const p = newPhone.trim();
    if (!p) return;
    if (notifyRows.some((r) => r.phone.replace(/\D/g, '') === p.replace(/\D/g, ''))) { setNewPhone(''); return; }
    setNotifyRows((rows) => [...rows, { phone: p, kinds: ['ticket'] }]);
    setNewPhone('');
  };

  const removePhone = (phone) => setNotifyRows((rows) => rows.filter((r) => r.phone !== phone));

  const saveNotify = async () => {
    setNotifyBusy(true); setNotifyMsg(null);
    try {
      const c = await ticketService.setNotifyConfig(notifyRows);
      setNotifyRows(c.numeros || []);
      setNotifyMsg({ ok: true, text: `Salvo: ${(c.numeros || []).length} número(s).` });
    } catch (e) {
      setNotifyMsg({ ok: false, text: e?.response?.data?.detail || 'Erro ao salvar.' });
    } finally { setNotifyBusy(false); }
  };

  const testNotify = async () => {
    setNotifyBusy(true); setNotifyMsg(null);
    try {
      const r = await ticketService.sendNotifyTest();
      const via = (r.results || []).map((x) => `${x.phone}: ${x.success ? 'enviado' : 'falhou'} (${x.mode || '-'})`).join(' · ');
      setNotifyMsg({ ok: r.sent > 0, text: r.sent > 0 ? `Teste enviado. ${via}` : `Nada enviado: ${r.skipped || via || 'sem retorno'}` });
    } catch (e) {
      setNotifyMsg({ ok: false, text: e?.response?.data?.detail || 'Erro ao enviar teste.' });
    } finally { setNotifyBusy(false); }
  };

  const totalPages = Math.max(1, Math.ceil(tickets.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const startIdx = (currentPage - 1) * PAGE_SIZE;
  const pageItems = tickets.slice(startIdx, startIdx + PAGE_SIZE);

  const handleUpdateStatus = async (ticketId, newStatus, agentNotes = null) => {
    try {
      const updated = await ticketService.updateStatus(ticketId, newStatus, agentNotes);
      setTickets(prev => prev.map(t => (t.id === ticketId ? updated : t)));
      if (selectedTicket?.id === ticketId) setSelectedTicket(updated);
      loadTickets();
    } catch (e) {
      alert('Erro ao atualizar ticket: ' + (e.message || 'desconhecido'));
    }
  };

  return (
    <div style={{
      flex: 1, display: 'flex', flexDirection: 'column',
      backgroundColor: colors.bgPrimary, overflowY: 'auto',
    }}>
      {/* Header */}
      <div style={{
        padding: '20px 24px', borderBottom: `1px solid ${colors.borderColor}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: '12px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <LifeBuoy size={22} color={colors.accentPrimary} />
          <h1 style={{ margin: 0, fontSize: '20px', color: colors.textPrimary, fontWeight: 600 }}>
            Tickets de Suporte
          </h1>
        </div>
        <button
          onClick={loadTickets}
          disabled={loading}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '8px 14px', borderRadius: '8px',
            border: `1px solid ${colors.borderColor}`,
            backgroundColor: colors.bgSecondary, color: colors.textPrimary,
            fontSize: '13px', cursor: loading ? 'default' : 'pointer',
            opacity: loading ? 0.6 : 1,
          }}
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Atualizar
        </button>
      </div>

      {/* Counts badges */}
      <div style={{
        padding: '16px 24px', display: 'flex', gap: '10px', flexWrap: 'wrap',
        borderBottom: `1px solid ${colors.borderColor}`,
      }}>
        {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
          const Icon = cfg.icon;
          return (
            <div
              key={key}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '10px 14px', borderRadius: '10px',
                backgroundColor: `${cfg.color}15`,
                border: `1px solid ${cfg.color}40`,
                minWidth: '120px',
              }}
            >
              <Icon size={16} color={cfg.color} />
              <div>
                <div style={{ fontSize: '11px', color: colors.textSecondary, textTransform: 'uppercase' }}>
                  {cfg.label}
                </div>
                <div style={{ fontSize: '18px', fontWeight: 600, color: cfg.color }}>
                  {counts[key] ?? 0}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Avisos por WhatsApp para a equipe */}
      <div style={{ margin: '0 24px 12px', padding: '14px 16px', borderRadius: 12, border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgSecondary }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: colors.textPrimary, marginBottom: 4 }}>Avisos por WhatsApp para a equipe</div>
        <div style={{ fontSize: 12, color: colors.textSecondary, marginBottom: 10, lineHeight: 1.5 }}>
          Escolha quem recebe e o que cada um recebe. Quem for adicionado agora recebe apenas os chamados de suporte.
        </div>

        {notifyRows.length === 0 && (
          <div style={{ fontSize: 12, color: colors.textSecondary, marginBottom: 8 }}>Nenhum número cadastrado.</div>
        )}

        {notifyRows.map((row) => (
          <div key={row.phone} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '8px 0', borderTop: `1px solid ${colors.borderColor}` }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: colors.textPrimary, minWidth: 130 }}>{row.phone}</span>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', flex: 1 }}>
              {Object.entries(notifyTypes).map(([kind, desc]) => (
                <label key={kind} title={desc} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: colors.textSecondary, cursor: 'pointer' }}>
                  <input type="checkbox" checked={(row.kinds || []).includes(kind)} onChange={() => toggleKind(row.phone, kind)} />
                  {({ ticket: 'Chamados', wa_connection: 'Queda de conexão', wa_reconnected: 'Reconexão', monitor_compra: 'Teste de compra', teste: 'Teste manual', limite_ia: 'IA sem responder', falha_material: 'Material não entregue' })[kind] || desc || kind}
                </label>
              ))}
            </div>
            <button onClick={() => removePhone(row.phone)} style={{ border: 'none', background: 'transparent', color: '#ef4444', cursor: 'pointer', fontSize: 12 }}>Remover</button>
          </div>
        ))}

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 12 }}>
          <input
            value={newPhone}
            onChange={(e) => setNewPhone(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addPhone(); }}
            placeholder="Adicionar número, ex: 5521999990000"
            style={{ flex: 1, minWidth: 220, padding: '8px 12px', borderRadius: 8, border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgPrimary, color: colors.textPrimary, fontSize: 13 }}
          />
          <button onClick={addPhone} style={{ padding: '8px 14px', borderRadius: 8, border: `1px solid ${colors.borderColor}`, backgroundColor: 'transparent', color: colors.textPrimary, fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>Adicionar</button>
          <button onClick={saveNotify} disabled={notifyBusy} style={{ padding: '8px 14px', borderRadius: 8, border: 'none', backgroundColor: '#10b981', color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>Salvar</button>
          <button onClick={testNotify} disabled={notifyBusy} style={{ padding: '8px 14px', borderRadius: 8, border: `1px solid ${colors.borderColor}`, backgroundColor: 'transparent', color: colors.textPrimary, fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>Enviar teste</button>
        </div>
        {notifyMsg && (
          <div style={{ marginTop: 8, fontSize: 12, color: notifyMsg.ok ? '#22c55e' : '#ef4444' }}>{notifyMsg.text}</div>
        )}
      </div>

      {/* Filters */}
      <div style={{
        padding: '16px 24px', display: 'flex', gap: '12px', flexWrap: 'wrap',
        alignItems: 'center', borderBottom: `1px solid ${colors.borderColor}`,
      }}>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {FILTER_OPTIONS.map(f => (
            <button
              key={f.id}
              onClick={() => { setStatusFilter(f.id); setPage(1); }}
              style={{
                padding: '7px 14px', borderRadius: '18px', border: 'none',
                backgroundColor: statusFilter === f.id ? colors.accentPrimary : colors.bgTertiary,
                color: statusFilter === f.id ? '#fff' : colors.textSecondary,
                fontSize: '13px', fontWeight: 500, cursor: 'pointer',
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          padding: '6px 10px', borderRadius: '8px',
          backgroundColor: colors.bgSecondary,
          border: `1px solid ${colors.borderColor}`,
          flex: '1 1 240px', maxWidth: '320px',
        }}>
          <Search size={14} color={colors.textSecondary} />
          <input
            type="text"
            placeholder="Buscar por telefone..."
            value={phoneFilter}
            onChange={(e) => { setPhoneFilter(e.target.value); setPage(1); }}
            style={{
              flex: 1, border: 'none', outline: 'none', background: 'transparent',
              color: colors.textPrimary, fontSize: '13px',
            }}
          />
        </div>
      </div>

      {/* Table */}
      <div style={{ padding: '16px 24px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: colors.textSecondary }}>
            Carregando...
          </div>
        ) : tickets.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: '60px 20px', color: colors.textSecondary,
          }}>
            <LifeBuoy size={40} color={colors.textTertiary} style={{ marginBottom: '12px' }} />
            <div style={{ fontSize: '14px' }}>Nenhum ticket encontrado</div>
          </div>
        ) : (
          <div style={{
            backgroundColor: colors.bgSecondary, borderRadius: '12px',
            border: `1px solid ${colors.borderColor}`, overflow: 'hidden',
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${colors.borderColor}` }}>
                  <th style={{ textAlign: 'left', padding: '10px 14px', color: colors.textSecondary, fontWeight: 500, fontSize: '12px' }}>
                    Telefone
                  </th>
                  <th style={{ textAlign: 'left', padding: '10px 14px', color: colors.textSecondary, fontWeight: 500, fontSize: '12px' }}>
                    Titulo
                  </th>
                  <th style={{ textAlign: 'left', padding: '10px 14px', color: colors.textSecondary, fontWeight: 500, fontSize: '12px' }}>
                    Motivo
                  </th>
                  <th style={{ textAlign: 'center', padding: '10px 14px', color: colors.textSecondary, fontWeight: 500, fontSize: '12px' }}>
                    Status
                  </th>
                  <th style={{ textAlign: 'right', padding: '10px 14px', color: colors.textSecondary, fontWeight: 500, fontSize: '12px' }}>
                    Criado em
                  </th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((t) => {
                  const cfg = STATUS_CONFIG[t.status] || STATUS_CONFIG.open;
                  return (
                    <tr
                      key={t.id}
                      onClick={() => setSelectedTicket(t)}
                      style={{
                        borderBottom: `1px solid ${colors.borderColor}20`,
                        cursor: 'pointer',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.bgTertiary}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <td style={{ padding: '12px 14px', color: colors.textPrimary, fontFamily: 'monospace', fontSize: '12px' }}>
                        {t.phone}
                      </td>
                      <td style={{ padding: '12px 14px', color: colors.textPrimary, maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {t.title}
                      </td>
                      <td style={{ padding: '12px 14px', color: colors.textSecondary, fontSize: '12px' }}>
                        {t.reason}
                      </td>
                      <td style={{ padding: '12px 14px', textAlign: 'center' }}>
                        <span style={{
                          padding: '3px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: 600,
                          backgroundColor: `${cfg.color}20`, color: cfg.color,
                        }}>
                          {cfg.label}
                        </span>
                      </td>
                      <td style={{ padding: '12px 14px', textAlign: 'right', color: colors.textSecondary, fontSize: '12px' }}>
                        {t.created_at
                          ? new Date(t.created_at).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
                          : '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: '12px', marginTop: '16px',
          }}>
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              style={{
                display: 'flex', alignItems: 'center', gap: '4px',
                padding: '6px 12px', fontSize: '12px',
                border: `1px solid ${colors.borderColor}`, borderRadius: '6px',
                backgroundColor: 'transparent', color: colors.textPrimary,
                opacity: currentPage === 1 ? 0.4 : 1,
                cursor: currentPage === 1 ? 'default' : 'pointer',
              }}
            >
              <ChevronLeft size={14} /> Anterior
            </button>
            <span style={{ fontSize: '13px', color: colors.textSecondary }}>
              {currentPage} de {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              style={{
                display: 'flex', alignItems: 'center', gap: '4px',
                padding: '6px 12px', fontSize: '12px',
                border: `1px solid ${colors.borderColor}`, borderRadius: '6px',
                backgroundColor: 'transparent', color: colors.textPrimary,
                opacity: currentPage === totalPages ? 0.4 : 1,
                cursor: currentPage === totalPages ? 'default' : 'pointer',
              }}
            >
              Proximo <ChevronRight size={14} />
            </button>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedTicket && (
        <TicketDetailModal
          ticket={selectedTicket}
          colors={colors}
          onClose={() => setSelectedTicket(null)}
          onUpdateStatus={handleUpdateStatus}
        />
      )}
    </div>
  );
};


const TicketDetailModal = ({ ticket, colors, onClose, onUpdateStatus }) => {
  const [notes, setNotes] = useState(ticket.agent_notes || '');
  const cfg = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.open;
  const Icon = cfg.icon;

  const actions = [
    { status: 'in_progress', label: 'Iniciar atendimento', color: '#f59e0b' },
    { status: 'resolved', label: 'Marcar como resolvido', color: '#22c55e' },
    { status: 'closed', label: 'Fechar ticket', color: '#64748b' },
    { status: 'open', label: 'Reabrir', color: '#ef4444' },
  ].filter(a => a.status !== ticket.status);

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
        zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px',
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: colors.bgPrimary, borderRadius: '12px',
          border: `1px solid ${colors.borderColor}`,
          width: '100%', maxWidth: '640px', maxHeight: '85vh',
          overflow: 'auto', padding: 0,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '20px 24px', borderBottom: `1px solid ${colors.borderColor}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Icon size={22} color={cfg.color} />
            <div>
              <div style={{ fontSize: '16px', fontWeight: 600, color: colors.textPrimary }}>
                {ticket.title}
              </div>
              <div style={{ fontSize: '12px', color: colors.textSecondary, fontFamily: 'monospace', marginTop: '2px' }}>
                {ticket.phone}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              border: 'none', background: 'transparent', cursor: 'pointer',
              color: colors.textSecondary, padding: '4px',
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 24px' }}>
          {/* Status badge */}
          <div style={{ marginBottom: '16px' }}>
            <span style={{
              padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: 600,
              backgroundColor: `${cfg.color}20`, color: cfg.color,
              display: 'inline-flex', alignItems: 'center', gap: '6px',
            }}>
              <Icon size={12} /> {cfg.label}
            </span>
          </div>

          {/* Motivo */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '11px', color: colors.textSecondary, textTransform: 'uppercase', marginBottom: '4px' }}>
              Motivo
            </div>
            <div style={{ fontSize: '14px', color: colors.textPrimary }}>
              {ticket.reason}
            </div>
          </div>

          {/* Descricao */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '11px', color: colors.textSecondary, textTransform: 'uppercase', marginBottom: '4px' }}>
              Descricao
            </div>
            <div style={{
              fontSize: '14px', color: colors.textPrimary,
              padding: '10px 12px', backgroundColor: colors.bgSecondary,
              borderRadius: '8px', whiteSpace: 'pre-wrap',
            }}>
              {ticket.description}
            </div>
          </div>

          {/* Notas internas */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '11px', color: colors.textSecondary, textTransform: 'uppercase', marginBottom: '4px' }}>
              Notas internas
            </div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Adicione notas sobre o atendimento..."
              style={{
                width: '100%', minHeight: '80px', padding: '10px 12px',
                fontSize: '14px', color: colors.textPrimary,
                backgroundColor: colors.bgSecondary,
                border: `1px solid ${colors.borderColor}`,
                borderRadius: '8px', resize: 'vertical', outline: 'none',
                fontFamily: 'inherit',
              }}
            />
          </div>

          {/* Metadata */}
          <div style={{
            display: 'flex', gap: '20px', fontSize: '12px', color: colors.textSecondary,
            paddingBottom: '16px', borderBottom: `1px solid ${colors.borderColor}`,
          }}>
            <div>
              <strong>Criado em:</strong>{' '}
              {ticket.created_at
                ? new Date(ticket.created_at).toLocaleString('pt-BR')
                : '-'}
            </div>
            {ticket.updated_at && (
              <div>
                <strong>Atualizado em:</strong>{' '}
                {new Date(ticket.updated_at).toLocaleString('pt-BR')}
              </div>
            )}
          </div>

          {/* Actions */}
          <div style={{
            marginTop: '16px', display: 'flex', gap: '8px', flexWrap: 'wrap',
          }}>
            {actions.map(a => (
              <button
                key={a.status}
                onClick={() => onUpdateStatus(ticket.id, a.status, notes)}
                style={{
                  padding: '8px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: 500,
                  backgroundColor: a.color, color: '#fff',
                  border: 'none', cursor: 'pointer',
                }}
              >
                {a.label}
              </button>
            ))}
            <button
              onClick={() => onUpdateStatus(ticket.id, ticket.status, notes)}
              style={{
                padding: '8px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: 500,
                backgroundColor: 'transparent', color: colors.textPrimary,
                border: `1px solid ${colors.borderColor}`, cursor: 'pointer',
              }}
            >
              Salvar notas
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TicketsPage;
